// src/components/header.ts
// Ergebnis: Mega-Menu Dropdown "Tankanlagenbau" (SAP-Style) wiederhergestellt
// ohne andere Anpassungen zu verlieren: Sticky Shadow, Corporate Gradient, User Dropdown (Avatar/Status/Profil/PW/Logout)

import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { router, resolveRouterPath } from "../core/router";
import { APPBACKEND } from "../core/config";
import {
  isLoggedIn,
  logout,
  getDisplayName,
  getSession,
  startSilentSessionRefresh,
  getLastRefreshOk,
  hasRole,
  getSessionExpiresInMs,
  getSessionHealth,
} from "../core/auth";

// Shoelace (User dropdown)
import "@shoelace-style/shoelace/dist/components/dropdown/dropdown.js";
import "@shoelace-style/shoelace/dist/components/menu/menu.js";
import "@shoelace-style/shoelace/dist/components/menu-item/menu-item.js";
import "@shoelace-style/shoelace/dist/components/divider/divider.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";


import logoI from "/logo_ilzhoefer.png";
import logoA from "/logo_avia.png";
import { DEVICE } from "../core/device";

@customElement("app-header")
export class AppHeader extends LitElement {
  static styles = css`
    :host {
      display: block;
      height: 120px;
      background: #fff;
      font-family: var(--font-corporate);
      width: 100%;
    }

    /* ===== FIXED HEADER WRAPPER ===== */
    .myHeaderContent {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      z-index: 10000;
      box-sizing: border-box;

      /* Corporate gradient (subtle) */

      transition: box-shadow 160ms ease;
    }

    .navbar {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 60px;
      padding-top: 73px;
      background: #fff;
      border: none;
      z-index: 9000;
      box-sizing: border-box;
      transition: box-shadow 160ms ease;
    }

    /* Sticky shadow when scrolling */
    .scrolled {
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.10);
    }

    /* ===== FULL WIDTH CONTAINER ===== */
    .container {
      width: 100%;
      max-width: 100%;
      margin: 0;
      padding: 0 24px;
      box-sizing: border-box;
    }

    /* Ultra-wide cap */
    .container-inner {
      max-width: 1600px;
      margin: 0 auto;
      width: 100%;
    }

    .myHeader {
      padding: 10px 0;
    }

    .Logo-Header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .avia-logo {
      display: block;
      height: 54px;
      width: auto;
    }

    .navbar-inner {
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      width: 100%;
    }

    .nav {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      gap: 18px;
      align-items: center;
    }

    /* Buttons wie im Screenshot inkl. invert-on-hover */
    .nav-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 190px;
      height: 34px;
      padding: 0 16px;
      background: var(--avia-red);
      border: 1px solid var(--avia-red);
      color: #fff;
      text-decoration: none;
      font-weight: 700;
      font-size: 14px;
      letter-spacing: 0.2px;
      user-select: none;
      cursor: pointer;
      box-sizing: border-box;
      transition: background-color 120ms ease, color 120ms ease;
      position: relative;
    }

    .nav-btn:hover {
      background: #fff;
      color: var(--avia-red);
    }

    .nav-btn:active {
      transform: translateY(1px);
    }

    /* ===== User Cluster (Avatar + Name + Online Dot) ===== */
    .user-trigger {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      height: 34px;
      padding: 0 12px;
      min-width: auto;
    }

    .avatar {
      width: 26px;
      height: 26px;
      border-radius: 999px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-weight: 900;
      font-size: 12px;
      background: rgba(255,255,255,0.25);
      border: 1px solid rgba(255,255,255,0.65);
      color: #fff;
      box-sizing: border-box;
      flex: 0 0 auto;
    }

    .user-name {
      max-width: 240px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .status-dot {
      width: 10px;
      height: 10px;
      border-radius: 999px;
      border: 1px solid rgba(255,255,255,0.65);
      box-sizing: border-box;
      flex: 0 0 auto;
    }

    /* online (green) / offline (gray) / session-problem (amber) */
    .dot-online { background: #21c55d; }
    .dot-offline { background: #9ca3af; }
    .dot-warn { background: #f59e0b; }
    .dot-expire { background: #ef4444; }

    /* Shoelace dropdown panel styling */
    sl-dropdown::part(panel) {
      background: #fff;
      border: 1px solid rgba(0,0,0,0.12);
      border-radius: 12px;
      box-shadow: 0 18px 45px rgba(0,0,0,0.18);
      padding: 6px;
      min-width: 260px;
    }

    sl-menu-item::part(base) {
      border-radius: 10px;
      font-weight: 800;
    }

    /* ===================== Mega-Menu Dropdown (SAP-style) ===================== */
    .dropdown {
      position: relative;
    }

    .dropdown-toggle {
      padding-right: 34px; /* room for caret */
    }

    .caret {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 6px solid #fff; /* on red */
      transition: border-top-color 120ms ease, transform 120ms ease;
    }

    .nav-btn:hover .caret {
      border-top-color: var(--avia-red); /* invert like button */
    }

    .dropdown.open .caret {
      transform: translateY(-50%) rotate(180deg);
    }

    .mega-backdrop {
      position: fixed;
      inset: 0;
      z-index: 11000;
      background: transparent;
      display: none;
    }

    .dropdown.open .mega-backdrop {
      display: block;
    }

    .mega {
      position: absolute;
      top: calc(100% + 10px);
      left: 0;
      min-width: 520px;
      background: #fff;
      border-radius: 14px;
      border: 1px solid rgba(0, 0, 0, 0.14);
      box-shadow: 0 18px 45px rgba(0, 0, 0, 0.18);
      padding: 14px;
      display: none;
      z-index: 12000;
    }

    .dropdown.open .mega {
      display: block;
    }

    .mega-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px 14px;
    }

    .mega-item {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      padding: 10px 10px;
      border-radius: 12px;
      text-decoration: none;
      color: rgba(0, 0, 0, 0.90);
      border: 1px solid transparent;
      background: rgba(0, 0, 0, 0.02);
      transition: background 120ms ease, border-color 120ms ease, transform 120ms ease;
      cursor: pointer;
    }

    .mega-item:hover {
      background: rgba(227, 6, 19, 0.06);
      border-color: rgba(227, 6, 19, 0.25);
      transform: translateY(-1px);
    }

    .mega-ico {
      width: 34px;
      height: 34px;
      border-radius: 10px;
      background: rgba(227, 6, 19, 0.10);
      border: 1px solid rgba(227, 6, 19, 0.25);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      font-weight: 900;
      color: var(--avia-red);
    }

    .mega-ico-green {
      width: 34px;
      height: 34px;
      border-radius: 10px;
      background: rgba(76, 189, 66, 0.10);
      border: 1px solid rgba(76, 189, 66, 0.25);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      font-weight: 900;
      color: rgba(76, 189, 66, 0.75);
    }

    .mega-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .mega-title {
      font-weight: 900;
      font-size: 13px;
      line-height: 1.2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .mega-desc {
      font-size: 12px;
      opacity: 0.78;
      line-height: 1.25;
    }

    /* ===== RESPONSIVE ===== */
    @media (max-width: 900px) {
      .navbar-inner {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
      }

      .nav {
        flex-wrap: wrap;
        gap: 10px;
      }

      .nav-btn {
        min-width: 140px;
      }

      .user-name {
        max-width: 180px;
      }

      .mega {
        min-width: 320px;
        width: min(92vw, 520px);
      }

      .mega-grid {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 640px) {
      .avia-logo { height: 42px; }
    }

    .nav-btn.active {
      background: #fff;
      color: var(--avia-red);
      box-shadow: inset 0 0 0 2px var(--avia-red);
    }

    /* ===== Active Highlight im Mega-Menu ===== */
    .mega-item.active {
      background: rgba(227, 6, 19, 0.10);
      border-color: rgba(227, 6, 19, 0.45);
    }

    .mega-item.active .mega-ico {
      background: rgba(227, 6, 19, 0.18);
      border-color: rgba(227, 6, 19, 0.55);
    }

    .mega-item.active .mega-title {
      color: var(--avia-red);
    }

    /* ===== NAV DIVIDER (Enterprise Gradient Line) ===== */
    .nav-divider {
      position: fixed;
      top: 120px; /* exakt unter Header + Navbar */
      left: 0;
      width: 100%;
      height: 2px;
      z-index: 8500;
      pointer-events: none;

      background: linear-gradient(
        90deg,
        rgba(227, 6, 19, 0.12) 0%,
        rgba(0, 0, 0, 0.05) 50%,
        rgba(227, 6, 19, 0.12) 100%
      );

      opacity: 0.45;
      transition:
        opacity 160ms ease,
        height 160ms ease,
        background 160ms ease;
    }

    /* 🔥 stärker beim Scrollen */
    .nav-divider.scrolled {
      opacity: 1;
      height: 3px;
      background: linear-gradient(
        90deg,
        rgba(227, 6, 19, 0.35) 0%,
        rgba(0, 0, 0, 0.15) 50%,
        rgba(227, 6, 19, 0.35) 100%
      );
    }

    body.nav-scrolled .nav-divider {
      opacity: 1;
      height: 3px;
      background: linear-gradient(
      90deg,
      rgba(227, 6, 19, 0.35) 0%,
      rgba(0, 0, 0, 0.15) 50%,
      rgba(227, 6, 19, 0.35) 100%
      );
    }

    .mobile-nav {
      position: relative;
    }

    /* 🔥 wichtig */
    .mobile-nav.right {
      width: 100%;
      display: flex;
      justify-content: flex-end;
    }

    .hamburger {
      font-size: 22px;
      background: var(--avia-red);
      color: #fff;
      border: none;
      padding: 6px 12px;
      border-radius: 6px;
      cursor: pointer;
    }

    .mobile-menu {
      position: absolute;
      top: 50px;
      right: 0;

      background: #fff;
      border-radius: 10px;
      border: 1px solid rgba(0,0,0,0.15);

      box-shadow: 0 10px 30px rgba(0,0,0,0.2);

      display: flex;
      flex-direction: column;

      min-width: 180px;
      z-index: 20000;
    }

    .mobile-menu a {
      padding: 12px;
      text-decoration: none;
      color: #000;
      font-weight: 600;
      border-bottom: 1px solid rgba(0,0,0,0.05);
    }

    .mobile-menu a:hover {
      background: rgba(227, 6, 19, 0.08);
    }
  `;

  @state() private loggedIn = isLoggedIn();
  @state() private scrolled = false;

  @state() private displayName = getDisplayName();
  @state() private initials = this.computeInitials(this.displayName);

  // online status
  @state() private browserOnline = navigator.onLine;
  @state() private lastRefreshOk = getLastRefreshOk();

  private static cachedLogoI: string | null = null;
  private static cachedLogoA: string | null = null;

  @state() private logoBase64I: string | null = null;
  @state() private logoBase64A: string | null = null;

  // Mega-Menu state
  @state() private megaOpen = false;

  // Schalter für mobiles Menü
  @state() private mobileMenuOpen = false;

  // ✅ URLs (bei Bedarf anpassen)
  private readonly PROFILE_ROUTE = resolveRouterPath("profil");

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("keydown", this.onKeyDown);
    startSilentSessionRefresh({ intervalMs: 60_000, immediate: false });
    window.addEventListener("storage", this.onStorage);
    window.addEventListener("auth-changed", this.onAuthChanged as EventListener);
    window.addEventListener("online-changed", this.onOnlineChanged as EventListener);
    window.addEventListener("scroll", this.onScroll, { passive: true });

    this.refreshUser();
    this.scrolled = window.scrollY > 2;

    this.init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("storage", this.onStorage);
    window.removeEventListener("auth-changed", this.onAuthChanged as EventListener);
    window.removeEventListener("online-changed", this.onOnlineChanged as EventListener);
    window.removeEventListener("scroll", this.onScroll);
  }
  private onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") this.megaOpen = false;
  };

  private megaTimer?: number;

  private isActiveMega(...paths: string[]) {
    return paths.some(p => location.pathname.startsWith(p));
  }


  private toggleMega = (e: Event) => {
    e.preventDefault();
    clearTimeout(this.megaTimer);
    this.megaOpen = !this.megaOpen;
  };

  private isActive(path: string) {
    const current = location.pathname.replace(/\/+$/, "");
    const target = path.replace(/\/+$/, "");

    // Home: nur exakt matchen
    if (target === "") {
      return current === "";
    }

    return current === target || current.startsWith(target + "/");
  }

  private closeMega = () => {
    clearTimeout(this.megaTimer);
    this.megaTimer = window.setTimeout(() => {
      this.megaOpen = false;
    }, 120);
  };

  // private hasRole(allowed: number[]) {
  //   const s = getSession();
  //   return !!s && allowed.includes(Number(s.BenutzerTyp));
  // }

  private onScroll = () => {
    const v = window.scrollY > 2;
    if (v !== this.scrolled){
      this.scrolled = v;
      document.body.classList.toggle("nav-scrolled", v);
    }
  };

  private onStorage = (e: StorageEvent) => {
    if (e.key === "auth.loggedIn" || e.key === "auth.username" || e.key === "auth.session") {
      this.refreshUser();
    }
  };

  private onAuthChanged = () => {
    this.refreshUser();
  };

  private onOnlineChanged = () => {
    this.browserOnline = navigator.onLine;
    this.lastRefreshOk = getLastRefreshOk();
  };

  private refreshUser() {
    this.loggedIn = isLoggedIn();

    const name = getDisplayName();
    const s = getSession();

    const fallback =
      s?.Vorname && s?.Nachname ? `${s.Vorname} ${s.Nachname}` : (s?.Benutzername || "");

    this.displayName = name || fallback;
    this.initials = this.computeInitials(this.displayName || "User");

    this.requestUpdate();
  }

  private computeInitials(name: string) {
    const n = (name || "").trim();
    if (!n) return "U";

    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();

    const dot = n.split(/[.\-_]/).filter(Boolean);
    if (dot.length >= 2) return (dot[0][0] + dot[1][0]).toUpperCase();

    return (n[0] || "U").toUpperCase();
  }

  private go(path: string, e: Event) {
    e.preventDefault();
    this.mobileMenuOpen = false; // 🔥 wichtig
    this.megaOpen = false;
    router.navigate(path);
  }

  private async onLogout(e: Event) {
    e.preventDefault();
    this.megaOpen = false;
    logout();
    window.dispatchEvent(new CustomEvent("auth-changed"));
    router.navigate(resolveRouterPath("login"));
  }

  private getDotClass() {
    const state = getSessionHealth();

    switch (state) {
      case "ok": return "dot-online";
      case "expiring": return "dot-warn";
      case "warning": return "dot-warn";
      case "expired": return "dot-expire";
      case "offline": return "dot-offline";
      default: return "dot-offline";
    }
  }

  private getStatusText() {
    const state = getSessionHealth();

    if (state === "expiring") {
      const min = Math.ceil(getSessionExpiresInMs() / 60000);
      return `Session läuft in ${min} Min. ab`;
    }

    if (state === "expired") return "Session abgelaufen";
    if (state === "warning") return "Session instabil";
    if (state === "offline") return "Offline";

    return "Angemeldet";
  }

  private async loadImageAsBase64(src: string): Promise<string> {
    return new Promise((resolve, reject) => {

      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);

        const base64 = canvas.toDataURL("image/png");
        resolve(base64);
      };

      img.onerror = (err) => reject(err);

      img.src = src;
    });
  }

  private async init(){
    if (!AppHeader.cachedLogoI) {
      AppHeader.cachedLogoI = await this.loadImageAsBase64(logoI);
    }
    this.logoBase64I = AppHeader.cachedLogoI;

    if (!AppHeader.cachedLogoA) {
      AppHeader.cachedLogoA = await this.loadImageAsBase64(logoA);
    }
    this.logoBase64A = AppHeader.cachedLogoA;
  }

  render() {
    const homePath = resolveRouterPath();
    const loginPath = DEVICE.isMobile ? resolveRouterPath("mobile/login") : resolveRouterPath("login");
    const tankanlagenPath = resolveRouterPath("tankanlagen");
    const internPath = DEVICE.isMobile ? resolveRouterPath("mobile/intern") : resolveRouterPath("intern");
    const faqPath = DEVICE.isMobile ? resolveRouterPath("mobile/faq") : resolveRouterPath("faq");
    const supportPath = resolveRouterPath("support");
    const auftraegePath = resolveRouterPath("auftraege");
    const nachweisPath = resolveRouterPath("nachweis");
    const verwaltungPath = resolveRouterPath("verwaltung");
    const kioskPath = resolveRouterPath("management");
    const projektePath = resolveRouterPath("projekte");
    const aufgabenPath = resolveRouterPath("aufgaben");
    const triagePath = resolveRouterPath("triage");
    const triageAdminPath = resolveRouterPath("triage-admin");
    const triageSessionsPath = resolveRouterPath("triage-durchlaeufe");
    const todoPath = DEVICE.isMobile ? resolveRouterPath("mobile/todo") : resolveRouterPath("todo");
    const bautagebuchMobilePath = resolveRouterPath("mobile/bautagebuch");

    const headerCls = `myHeaderContent ${this.scrolled ? "scrolled" : ""}`.trim();
    const navbarCls = `navbar ${this.scrolled ? "scrolled" : ""}`.trim();

    return html`
      <div class=${headerCls}>
        <div class="container myHeader">
          <div class="container-inner">
            <div class="Logo-Header">
              <!-- <img class="avia-logo" src="${APPBACKEND}/ContentImages/logo_ilzhoefer.svg" alt="ILZHÖFER" /> -->
              <!-- <img class="avia-logo" src="${APPBACKEND}/ContentImages/logo_avia.svg" alt="AVIA" /> -->
              <img class="avia-logo" src="${this.logoBase64I}" alt="ILZHÖFER" />
              <img class="avia-logo" src="${this.logoBase64A}" alt="AVIA" />
            </div>
          </div>
        </div>
      </div>

      <div class=${navbarCls}>
        <div class="container">
          <div class="container-inner navbar-inner">
            ${DEVICE.isMobile
            ? html`
                <!-- MOBILE NAV -->
                <div class="mobile-nav right">

                  <button class="hamburger" @click=${() => this.mobileMenuOpen = !this.mobileMenuOpen}>
                    ☰
                  </button>

                  ${this.mobileMenuOpen
                    ? html`
                        <div class="mobile-menu">

                          <a @click=${(e: Event) => this.go(homePath, e)}>Startseite</a>
                          <a @click=${(e: Event) => this.go(internPath, e)}>Intern</a>
                          <a @click=${(e: Event) => this.go(todoPath, e)}>ToDo</a>
                          ${this.loggedIn && hasRole(5, 8) ? html`
                            <a @click=${(e: Event) => this.go(bautagebuchMobilePath, e)}>Bautagebuch</a>
                          ` : nothing}
                          <a @click=${(e: Event) => this.go(faqPath, e)}>FAQ</a>

                          ${this.loggedIn ? html`
                            <a @click=${(e: Event) => this.onLogout(e)}>Logout</a>
                            ` : html `
                            <a href=${loginPath} @click=${(e: Event) => this.go(loginPath, e)}>Login</a>
                            `}
                        </div>
                      `
                    : nothing}

                </div>
              `
            : html`
            <!-- DESKTOP NAV (dein bestehender Code 1:1 lassen!) -->
            <ul class="nav">
              <li>
                <a class="nav-btn ${this.isActive(homePath) ? 'active' : ''}" href=${homePath} @click=${(e: Event) => this.go(homePath, e)}>Startseite</a>
              </li>

              ${this.loggedIn
                ? html`
                    <!-- Mega-Menu Dropdown: Tankanlagenbau -->
                    <li class="dropdown ${this.megaOpen ? "open" : ""}">
                      ${this.megaOpen ? html`<div class="mega-backdrop" @click=${this.closeMega}></div>` : nothing}

                      <a class="nav-btn ${this.isActive(tankanlagenPath) ? 'active' : ''} ${this.isActive(internPath) ? 'active' : ''} dropdown-toggle" href=${tankanlagenPath} @click=${this.toggleMega}>
                        Tankanlagenbau
                        <span class="caret"></span>
                      </a>

                      <div class="mega" role="menu">
                        <div class="mega-grid">
                          <!-- Verwaltung -->
                          ${hasRole(5) ? html`
                            <a class="mega-item" href=${verwaltungPath} @click=${(e: Event) => this.go(verwaltungPath, e)}>
                              <span class="mega-ico">D</span>
                              <span class="mega-text">
                                <span class="mega-title">Dashboard</span>
                                <span class="mega-desc">Aufträge, Montagenachweise und Support-Tickets</span>
                              </span>
                            </a>
                          ` : nothing}

                          <!-- Projekte -->
                          ${hasRole(5, 8) ? html`
                            <a class="mega-item" href=${projektePath} @click=${(e: Event) => this.go(projektePath, e)}>
                              <span class="mega-ico">P</span>
                              <span class="mega-text">
                                <span class="mega-title">Projekte-Verwaltung</span>
                                <span class="mega-desc">Aufgaben, Zeitpläne und Inhalte</span>
                              </span>
                            </a>
                          ` : nothing}

                          <!-- Intern (immer sichtbar) -->
                          <a class="mega-item ${this.isActiveMega(internPath) ? 'active' : ''}" href=${internPath} @click=${(e: Event) => this.go(internPath, e)}>
                            <span class="mega-ico">I</span>
                            <span class="mega-text">
                              <span class="mega-title">Intern</span>
                              <span class="mega-desc">Tankanlagen-Details, Bilder und Dokumente</span>
                            </span>
                          </a>

                          <!-- ToDo (alle angemeldeten Benutzer) -->
                          <a class="mega-item ${this.isActiveMega(todoPath) ? 'active' : ''}" href=${todoPath} @click=${(e: Event) => this.go(todoPath, e)}>
                            <span class="mega-ico">✓</span>
                            <span class="mega-text">
                              <span class="mega-title">ToDo</span>
                              <span class="mega-desc">Meine ToDos ansehen und abhaken</span>
                            </span>
                          </a>

                          <!-- Aufgaben -->
                          ${hasRole(2, 3, 5, 6) ? html`
                            <a class="mega-item ${this.isActiveMega(aufgabenPath) ? 'active' : ''}" href=${aufgabenPath} @click=${(e: Event) => this.go(aufgabenPath, e)}>
                              <span class="mega-ico">A</span>
                              <span class="mega-text">
                                <span class="mega-title">Aufgaben</span>
                                <span class="mega-desc">einfache und wiederkehrende Aufgaben</span>
                              </span>
                            </a>
                          ` : nothing}

                          <!-- Tankanlagen -->
                          ${hasRole(1, 7, 5, 6) ? html`
                            <a class="mega-item ${this.isActiveMega(tankanlagenPath) ? 'active' : ''}" href=${tankanlagenPath} @click=${(e: Event) => this.go(tankanlagenPath, e)}>
                              <span class="mega-ico">T</span>
                              <span class="mega-text">
                                <span class="mega-title">Tankanlagen</span>
                                <span class="mega-desc">Tankanlagenübersicht</span>
                              </span>
                            </a>
                          ` : nothing}

                          <!-- Aufträge -->
                          ${hasRole(5, 6) ? html`
                            <a class="mega-item" href=${auftraegePath} @click=${(e: Event) => this.go(auftraegePath, e)}>
                              <span class="mega-ico">A</span>
                              <span class="mega-text">
                                <span class="mega-title">Aufträge</span>
                                <span class="mega-desc">Geplante/aktive Aufträge</span>
                              </span>
                            </a>
                          ` : nothing}

                          <!-- Montage -->
                          ${hasRole(5, 6) ? html`
                            <a class="mega-item" href=${nachweisPath} @click=${(e: Event) => this.go(nachweisPath, e)}>
                              <span class="mega-ico-green">M</span>
                              <span class="mega-text">
                                <span class="mega-title">Montage</span>
                                <span class="mega-desc">Montagenachweis</span>
                              </span>
                            </a>
                          ` : nothing}

                          <!-- Kiosk-Verwaltung -->
                          ${hasRole(5) ? html`
                            <a class="mega-item" href=${kioskPath} @click=${(e: Event) => this.go(kioskPath, e)}>
                              <span class="mega-ico">K</span>
                              <span class="mega-text">
                                <span class="mega-title">Kiosk-Verwaltung</span>
                                <span class="mega-desc">Tankstellen-Monitor-Verwaltung</span>
                              </span>
                            </a>
                          ` : nothing}

                          <!-- Triage-Verwaltung -->
                          ${hasRole(5) ? html`
                            <a class="mega-item" href=${triageAdminPath} @click=${(e: Event) => this.go(triageAdminPath, e)}>
                              <span class="mega-ico">T</span>
                              <span class="mega-text">
                                <span class="mega-title">Triage-Verwaltung</span>
                                <span class="mega-desc">Verwaltung der Abfrage-Flows</span>
                              </span>
                            </a>
                          ` : nothing}

                          <!-- Triage-Durchläufe -->
                          ${hasRole(5) ? html`
                            <a class="mega-item" href=${triageSessionsPath} @click=${(e: Event) => this.go(triageSessionsPath, e)}>
                              <span class="mega-ico">D</span>
                              <span class="mega-text">
                                <span class="mega-title">Triage-Durchläufe</span>
                                <span class="mega-desc">Übersicht und Auswertung der Gespräche</span>
                              </span>
                            </a>
                          ` : nothing}

                        </div>
                      </div>
                    </li>
                    ${hasRole(5,7) ? html`
                    <li>
                      <a
                        class="nav-btn ${this.isActive(triagePath) ? 'active' : ''}"
                        href=${triagePath}
                        @click=${(e: Event) => this.go(triagePath, e)}
                      >
                        TA-Unterstützung
                      </a>
                    </li>
                    ` : nothing}
                    <li>
                      <a
                        class="nav-btn ${this.isActive(supportPath) ? 'active' : ''}"
                        href=${supportPath}
                        @click=${(e: Event) => this.go(supportPath, e)}
                      >
                        Support
                      </a>
                    </li>
                    <li>
                      <a
                        class="nav-btn ${this.isActive(faqPath) ? 'active' : ''}"
                        href=${faqPath}
                        @click=${(e: Event) => this.go(faqPath, e)}
                      >
                        FAQ
                      </a>
                    </li>
                  `
                : nothing}

            </ul>

            <ul class="nav">
              ${this.loggedIn
                ? html`
                    <li>
                      <sl-dropdown placement="bottom-end" distance="8">
                        <a
                          slot="trigger"
                          class="nav-btn user-trigger"
                          href="#"
                          @click=${(e: Event) => e.preventDefault()}
                          title="Benutzer"
                        >
                          <span class="avatar">${this.initials}</span>
                          <span class="user-name">${this.displayName || "Benutzer"}</span>
                          <span class="status-dot ${this.getDotClass()}" title="${this.getStatusText()}"></span>
                          <sl-icon name="chevron-down" style="margin-left:2px;"></sl-icon>
                        </a>

                        <sl-menu>
                          <sl-menu-item @click=${(e: Event) => this.go(this.PROFILE_ROUTE, e)}>
                            <sl-icon slot="prefix" name="person"></sl-icon>
                              Profil
                          </sl-menu-item>

                          <sl-divider></sl-divider>

                          ${hasRole(5, 7) ? html`
                            <sl-menu-item @click=${(e: Event) => this.go(resolveRouterPath("benutzerverwaltung"), e)}>
                              <sl-icon slot="prefix" name="people"></sl-icon>
                              Benutzerverwaltung
                            </sl-menu-item>

                            <sl-divider></sl-divider>
                          ` : nothing}

                          <sl-menu-item @click=${() => {
                            window.dispatchEvent(new CustomEvent("dashboard-edit-toggle"));
                          }}>
                            <sl-icon slot="prefix" name="grid"></sl-icon>
                            Dashboard bearbeiten
                          </sl-menu-item>

                          <sl-divider></sl-divider>

                          <sl-menu-item @click=${(e: Event) => this.onLogout(e)}>
                            <sl-icon slot="prefix" name="box-arrow-right"></sl-icon>
                            Logout
                          </sl-menu-item>
                        </sl-menu>
                      </sl-dropdown>
                    </li>
                  `
                : html`
                    <li>
                      <a class="nav-btn" href=${loginPath} @click=${(e: Event) => this.go(loginPath, e)}>Login</a>
                    </li>
                  `}
            </ul>
            `}
          </div>
        </div>
      </div>
      <div class="nav-divider"></div>
    `;
  }
}
